/**
 * Client typé des phases de génération V2 « blocs natifs ».
 *
 * Chaque phase (cohérence / corrigé / audit / distracteurs / image) est une
 * edge function qui fait l'appel LLM, lit les blocs existants et RENVOIE des
 * opérations de blocs (`BlockMutationOp[]`). Le client les applique ensuite via
 * `exercise-blocks-mutate` (chemin d'écriture unique). La phase image renvoie un
 * prompt d'illustration, passé à `generate-illustration`.
 */

import { supabase } from '@/lib/supabase';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import type { BlockMutationOp, ExerciseBlock, ExerciseV2Payload } from './types';
import { buildStatementVisualsOps } from './visuals-attach';

export class V2GenerationError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'V2GenerationError';
  }
}

/** Invoque une edge function et extrait le message d'erreur métier du body. */
async function invokeOrThrow<T>(fn: string, body: Record<string, unknown> | FormData): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(fn, { body });
  if (error) {
    let message = error.message;
    let code: string | undefined;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as {
          error?: string;
          message?: string;
          reason?: string;
          op?: string;
        };
        code = parsed.error;
        if (parsed.message) message = parsed.message;
        else if (parsed.error) {
          // `exercise-blocks-mutate` renvoie { error: 'op_failed', op, reason } :
          // sans le `reason` l'utilisateur ne voit qu'un opaque « op_failed ».
          // On reconstitue un message lisible (quelle op, pourquoi).
          message = parsed.reason
            ? `${parsed.error} (${parsed.op ?? '?'}) : ${parsed.reason}`
            : parsed.error;
        }
      } catch {
        // garde le message générique
      }
    }
    throw new V2GenerationError(message, code);
  }
  if (!data) throw new V2GenerationError('empty_response');
  return data;
}

export interface PhaseOpsResult {
  ok: boolean;
  ops: BlockMutationOp[];
}
export interface CoherencePhaseResult extends PhaseOpsResult {
  coherence: { ok: boolean; issues: string[] };
}
export interface AuditPhaseResult extends PhaseOpsResult {
  audit_ok: boolean;
  fixed_count: number;
}

export function runCoherencePhase(
  exerciseId: string,
  rawStatement: string,
  customInstructions?: string,
): Promise<CoherencePhaseResult> {
  return invokeOrThrow<CoherencePhaseResult>('exercise-v2-coherence', {
    exercise_id: exerciseId,
    raw_statement: rawStatement,
    ...(customInstructions?.trim() ? { custom_instructions: customInstructions.trim() } : {}),
  });
}

export function runSolutionPhase(
  exerciseId: string,
  opts: { customInstructions?: string; revisionFeedback?: string; onlyMissing?: boolean } = {},
): Promise<PhaseOpsResult> {
  return invokeOrThrow<PhaseOpsResult>('exercise-v2-solution', {
    exercise_id: exerciseId,
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
    ...(opts.revisionFeedback?.trim() ? { revision_feedback: opts.revisionFeedback.trim() } : {}),
    ...(opts.onlyMissing ? { only_missing: true } : {}),
  });
}

/**
 * Structure une transcription orale (« corrigé dicté ») en blocs `correction`.
 * Mêmes options que `runSolutionPhase` + le `transcript` obligatoire. La sortie
 * est identique (corrections enfants des questions) → l'aval est inchangé.
 */
export function runDictationPhase(
  exerciseId: string,
  opts: {
    transcript: string;
    customInstructions?: string;
    onlyMissing?: boolean;
    /** Dictée ciblée sur une seule question (index 0-based). */
    questionIndex?: number;
  },
): Promise<PhaseOpsResult> {
  return invokeOrThrow<PhaseOpsResult>('exercise-v2-dictation', {
    exercise_id: exerciseId,
    transcript: opts.transcript.trim(),
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
    ...(opts.onlyMissing ? { only_missing: true } : {}),
    ...(opts.questionIndex != null ? { question_index: opts.questionIndex } : {}),
  });
}

/**
 * Chasse aux indices : génère un bloc `clue_hunt` enfant d'UNE question
 * (question_index ciblé, ou 1re éligible si omis). Le serveur renvoie aussi
 * `existing_clue_hunt_id` (id d'un clue_hunt déjà en place pour la même
 * question, à supprimer avant d'insérer le nouveau) et `no_clues_emitted`
 * (true si le LLM a jugé la chasse non pertinente).
 */
export function runClueHuntPhase(
  exerciseId: string,
  opts: { questionIndex?: number; customInstructions?: string } = {},
): Promise<PhaseOpsResult & { existing_clue_hunt_id: string | null; no_clues_emitted: boolean }> {
  return invokeOrThrow<
    PhaseOpsResult & { existing_clue_hunt_id: string | null; no_clues_emitted: boolean }
  >('exercise-v2-clue-hunt', {
    exercise_id: exerciseId,
    ...(opts.questionIndex != null ? { question_index: opts.questionIndex } : {}),
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
  });
}

/** Une question vue par `exercise-v2-scratch-model` : un modèle à passer à la porte, ou « hors champ ». */
export type ScratchModelDraftResult =
  | {
      question_block_id: string;
      question_index: number;
      eligible: true;
      model: unknown;
      notes?: string;
    }
  | {
      question_block_id: string;
      question_index: number;
      eligible: false;
      category: string;
      reason: string;
    };

export interface ScratchModelPhaseResult {
  ok: boolean;
  /** modèle utilisé pour cet appel (escalade comprise) */
  model?: string;
  engine_version: number;
  drafts: ScratchModelDraftResult[];
  skipped: { question_index: number; reason: string }[];
  question_count: number;
}

/** Rapport de la porte sur la tentative précédente d'une question (relance de la génération). */
export interface ScratchModelRetry {
  question_index: number;
  previous_model: unknown;
  issues: { code: string; message: string; line?: string }[];
}

/**
 * Feuilles de brouillon : l'IA propose un modèle par question (ou « hors champ »).
 * L'edge fn N'ÉCRIT RIEN : l'app passe chaque modèle à la porte du moteur, relance
 * au besoin avec le rapport (`retry`), puis enregistre `scratch_models`.
 */
export function runScratchModelPhase(
  exerciseId: string,
  opts: {
    questionIndexes: number[];
    retry?: ScratchModelRetry[];
    customInstructions?: string;
    /** relance « plus forte » (Opus) : honorée par le serveur seulement avec `retry` */
    escalate?: boolean;
    /** admin : chaque essai en Opus (ignoré par le serveur pour un tuteur) */
    forceOpus?: boolean;
  },
): Promise<ScratchModelPhaseResult> {
  return invokeOrThrow<ScratchModelPhaseResult>('exercise-v2-scratch-model', {
    exercise_id: exerciseId,
    question_indexes: opts.questionIndexes,
    ...(opts.retry?.length ? { retry: opts.retry } : {}),
    ...(opts.escalate && opts.retry?.length ? { escalate: true } : {}),
    ...(opts.forceOpus ? { force_opus: true } : {}),
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
  });
}

/** Édite UN bloc correction par la voix (patch ciblé). */
export function runDictationEditCorrection(
  exerciseId: string,
  opts: { correctionBlockId: string; transcript: string; customInstructions?: string },
): Promise<PhaseOpsResult> {
  return invokeOrThrow<PhaseOpsResult>('exercise-v2-dictation-edit', {
    exercise_id: exerciseId,
    correction_block_id: opts.correctionBlockId,
    transcript: opts.transcript.trim(),
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
  });
}

/** Génère une figure (visual_block) à partir d'une description orale, attachée au bloc. */
export function runDictateFigure(
  exerciseId: string,
  opts: { targetBlockId: string; transcript: string; customInstructions?: string },
): Promise<PhaseOpsResult> {
  return invokeOrThrow<PhaseOpsResult>('exercise-v2-dictate-figure', {
    exercise_id: exerciseId,
    target_block_id: opts.targetBlockId,
    transcript: opts.transcript.trim(),
    ...(opts.customInstructions?.trim()
      ? { custom_instructions: opts.customInstructions.trim() }
      : {}),
  });
}

/**
 * Transcrit un enregistrement audio de dictée via `transcribe-correction`
 * (Whisper). L'audio n'est jamais stocké : il transite dans le corps de la
 * requête. Renvoie le texte transcrit (éditable par le prof avant structuration).
 */
export async function transcribeDictation(audio: Blob): Promise<{ text: string }> {
  const form = new FormData();
  const ext = audio.type.includes('mp4') ? 'mp4' : 'webm';
  form.append('file', audio, `dictation.${ext}`);
  return invokeOrThrow<{ ok: boolean; text: string }>('transcribe-correction', form);
}

export function runAuditPhase(exerciseId: string): Promise<AuditPhaseResult> {
  return invokeOrThrow<AuditPhaseResult>('exercise-v2-audit', { exercise_id: exerciseId });
}

export function runDistractorsPhase(
  exerciseId: string,
  opts: { questionIndex?: number; onlyMissing?: boolean } = {},
): Promise<PhaseOpsResult> {
  return invokeOrThrow<PhaseOpsResult>('exercise-v2-distractors', {
    exercise_id: exerciseId,
    ...(opts.questionIndex !== undefined ? { question_index: opts.questionIndex } : {}),
    ...(opts.onlyMissing ? { only_missing: true } : {}),
  });
}

/**
 * Applique un batch d'ops via `exercise-blocks-mutate`. No-op si vide.
 * Renvoie le `client_id_map` (client_id d'insert → uuid réel) de l'edge fn —
 * indispensable au Blueprint pour construire son `block_map` (migration 0229).
 */
export async function applyBlockOps(
  exerciseId: string,
  ops: BlockMutationOp[],
): Promise<Record<string, string>> {
  if (ops.length === 0) return {};
  const res = await invokeOrThrow<{ ok: boolean; client_id_map?: Record<string, string> }>(
    'exercise-blocks-mutate',
    { exercise_id: exerciseId, ops },
  );
  return res.client_id_map ?? {};
}

/**
 * Rattache des visuels extraits d'images (via `extract-visual-blocks`) au bloc
 * `statement` de l'exercice V2. À appeler APRÈS la phase cohérence (qui crée le
 * bloc `statement`) : relit les blocs, construit l'op `update` puis l'applique.
 * No-op si la liste est vide ou si aucun bloc `statement` n'existe encore.
 */
export async function attachVisualsToStatement(
  exerciseId: string,
  visuals: ExerciseVisualBlock[],
): Promise<void> {
  if (visuals.length === 0) return;
  const payload = await invokeOrThrow<ExerciseV2Payload>('exercise-get-for-edit', {
    exercise_id: exerciseId,
  });
  const blocks: ExerciseBlock[] = payload.blocks ?? [];
  await applyBlockOps(exerciseId, buildStatementVisualsOps(blocks, visuals));
}

/** Génère le prompt d'image puis l'illustration (gpt-image-1). */
export async function runImagePhase(
  exerciseId: string,
  meta: { subject: string; level: string; title: string },
): Promise<{ illustration_url: string | null; image_prompt: string }> {
  const { image_prompt } = await invokeOrThrow<{ ok: boolean; image_prompt: string }>(
    'exercise-v2-image-prompt',
    { exercise_id: exerciseId },
  );
  const res = await invokeOrThrow<{ url?: string; illustration_url?: string }>(
    'generate-illustration',
    {
      prompt: image_prompt,
      entity_type: 'exercise',
      entity_id: exerciseId,
      subject: meta.subject,
      level: meta.level,
      title: meta.title,
    },
  );
  return { illustration_url: res.illustration_url ?? res.url ?? null, image_prompt };
}
